import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiInbox } from 'react-icons/fi';
import { CustomerCenterService } from "../../../services";
import TopSetion from '../../../components/customer-service-center/TopSetion';
import SubHeading from '../../../components/customer-service-center/SubHeading';
import { formatDateToYYYYMMDD } from '../../../utils/dateUtils';

// Utility function to format content and remove images
const formatContent = (htmlContent) => {
  if (!htmlContent) return '';

  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;

  // Remove all img tags
  const images = tempDiv.querySelectorAll('img');
  images.forEach(img => img.remove());

  // Get the text content and format it
  let textContent = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up extra whitespace and line breaks
  textContent = textContent
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\n\s*\n/g, '\n') // Replace multiple line breaks with single
    .trim();

  // Limit the content length for preview (e.g., 150 characters)
  if (textContent.length > 50) {
    textContent = textContent.substring(0, 50) + '...';
  }

  return textContent;
};

const AnnouncementList = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);


  const fetchAnnouncements = async (page = currentPage, append = false) => {
    try {
      setLoading(true);
      const apiData = await CustomerCenterService.getAnnouncements({
        page: page,
        size: 6,
      });
      if (apiData.content.length > 0) {
        // Check for duplicate boardId before setting announcements
        const uniqueAnnouncements = [];
        const seenBoardIds = new Set();
        
        // If appending, add existing announcements to the set
        if (append) {
          announcements.forEach(item => seenBoardIds.add(item.boardId));
        }
        
        for (const item of apiData.content) {
          if (!seenBoardIds.has(item.boardId)) {
            uniqueAnnouncements.push(item);
            seenBoardIds.add(item.boardId);
          }
        }
        
        if (append) {
          setAnnouncements(prev => [...prev, ...uniqueAnnouncements]);
        } else {
          setAnnouncements(uniqueAnnouncements);
        }
      }
      setTotalPages(apiData.totalPages);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAnnouncements();
  }, []); // Only run on component mount

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchAnnouncements(nextPage, true); // Append new announcements
    }
  };

  const handleAnnouncementClick = (announcementId) => {
    navigate(`/announcements/${announcementId}`);
  };

  return (

    <>
      <div className="announcement-list-page">

        <TopSetion />

        {/* Main Content */}
        <main className="main-content">

          <SubHeading heading="공지사항" />


          {/* Announcement Section */}
          <section className="announcement-section">
            {loading && announcements.length === 0 ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>공지사항을 불러오는 중...</p>
              </div>
            ) : announcements.length === 0 ? (
              <div className="empty-state">
                <FiInbox className="empty-icon" size={64} />
                <div className="empty-title">등록된 공지사항이 없습니다</div>
                <div className="empty-description">
                  새로운 공지사항이 등록되면 이곳에 표시됩니다.
                </div>
              </div>
            ) : (
              <>
                <div className="announcement-grid">
                  {announcements.map((announcement) => (
                    <div
                      key={announcement.boardId}
                      className="announcement-card clickable"
                      onClick={() => handleAnnouncementClick(announcement.boardId)}
                    >
                      <div className="announcement-image">
                        <img
                          src={announcement.attachUrls[0]}
                          alt={announcement.boardTitle}
                          onError={(e) => {
                            e.target.src = '/images/default-announcement.jpg';
                          }}
                        />
                      </div>
                      <div className="announcement-content">
                        <h3 className="announcement-title">
                          {announcement.boardTitle}
                        </h3>
                        <p className="announcement-description">
                          {formatContent(announcement.boardContents)}
                        </p>
                        <div className="announcement-meta">
                          <span className="announcement-date">
                            {formatDateToYYYYMMDD(announcement.registTime)}
                          </span>
                          <span className="announcement-views">
                            <FiEye className="eye-icon" />
                            {announcement.boardReadCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {currentPage < totalPages && (
                  <div className="more-button-section">
                    <button
                      className="more-button"
                      onClick={handleLoadMore}
                      disabled={loading}
                    >
                      {loading ? '로딩 중...' : '더보기'}
                    </button>
                  </div>
                )}

              </>
            )}
          </section>
        </main>
      </div>
    </>
  );
};

export default AnnouncementList;